import { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import useSpotDash2, {
  VIEW_TYPES,
  LOCATION_FILTER_TYPES,
} from "../../../hooks/useSpotDash2";
import styles from "./SpotDashboard.module.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { MyCompanyProfileContext } from "../../../MyCompanyProfileContext";
import { getImageSrc } from "../../../Utility";
import ProfileModal from "../../TransporterClientScreen/ProfileModal/ProfileModal";
import { UserApiContext } from "../../UserDetailsContext/UserDetailsContext";
import { useMixPanelForTracking } from "../../../utils/TrackMixPanel";
import DatePicker from "react-datepicker";
import { GRAD } from "../../../Constants";
import {
  formatLakhs,
  formatPercentage,
  formatNumber,
  getTodayDate,
  subtractMonthsFromDate,
  getLastSevenDaysStartDate,
  getViewTypeLabel,
  getUserEmail,
  getOriginName,
  getLaneLabel,
  getDestinationFromLane,
  shortEmail,
} from "./Utility";

import {
  KpiCard,
  ChartCard,
  Legend,
  FullscreenModal,
  ViewTypeDropdown,
  FilterDropdown,
  FsBtn,
} from "./SpotDashComponents";
import { HBarChart } from "./Horizontalbargraph";
import { DonutChart } from "./Donutchart";
import { LineChart } from "./Linechart";
import { VBarChart } from "./Verticalbargraph";
import { PieChart } from "./Piechart";


// import "react-datepicker/dist/react-datepicker.css";


export default function AdhocDash2() {
  const {
    loading,
    error,
    dashboardData,
    filters,
    userList,
    originList,
    laneList,
    fetchInitialDashboard,
    fetchDashboardByDate,
    fetchLocationData,
    clearError,
  } = useSpotDash2();

  const [viewType, setViewType] = useState(VIEW_TYPES.MY_ENQUIRIES);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [startDate, setStartDate] = useState(getLastSevenDaysStartDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [selectedOrigin, setSelectedOrigin] = useState("");
  const [selectedLane, setSelectedLane] = useState("");
  const [fsChart, setFsChart] = useState(null);
  const dashRef = useRef(null);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { userDetails } = useContext(UserApiContext);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    setLogoError(false);
  }, [companySettingData?.logo]);
  const { TrackMixPanel } = useMixPanelForTracking(
    userDetails,
    companySettingData,
  );

  const cards = dashboardData?.cards || {};
  const charts = dashboardData?.charts || {};

  const userOptions = useMemo(() => {
    return userList
      .map(user => getUserEmail(user))
      .filter(Boolean)
      .map(email => ({ value: email, label: email }));
  }, [userList]);

  const originOptions = useMemo(() => {
    return originList
      .map(item => getOriginName(item))
      .filter(Boolean)
      .map(origin => ({ value: origin, label: origin }));
  }, [originList]);

  const laneOptions = useMemo(() => {
    return laneList
      .map(item => {
        const label = getLaneLabel(item);
        const destination = getDestinationFromLane(item);

        return {
          value: label,
          label,
          destination,
        };
      })
      .filter(item => item.value);
  }, [laneList]);

  const loadInitialDashboard = useCallback(
    async nextViewType => {
      clearError();

      setSelectedUsers([]);
      setSelectedOrigin("");
      setSelectedLane("");

      const response = await fetchInitialDashboard({
        viewType: nextViewType,
      });

      if (response?.filters?.start_date) {
        setStartDate(response.filters.start_date);
      }

      if (response?.filters?.end_date) {
        setEndDate(response.filters.end_date);
      }
    },
    [clearError, fetchInitialDashboard]
  );

  useEffect(() => {
    loadInitialDashboard(VIEW_TYPES.MY_ENQUIRIES);
  }, [loadInitialDashboard]);

  const handleViewTypeChange = nextViewType => {
    setViewType(nextViewType);
    TrackMixPanel({
      ModuleName: "Spot",
      ScreenName: "Spot Dashboard",
      Data: ``,
      Definition:
        nextViewType === VIEW_TYPES.COMPANY_ENQUIRIES
          ? "User has changed enquiry dropdown to company enquiries"
          : "User has changed enquiry dropdown to my enquiries",
    });
    loadInitialDashboard(nextViewType);
  };

  const handleStartDateChange = async nextStartDate => {
    setStartDate(nextStartDate);
    setSelectedOrigin("");
    setSelectedLane("");

    TrackMixPanel({
      ModuleName: "Spot",
      ScreenName: "Spot Dashboard",
      Data: ``,
      Definition: "User has changed start date filter",
    })

    if (!nextStartDate || !endDate) return;

    await fetchDashboardByDate({
      viewType,
      startDate: nextStartDate,
      endDate,
      selectedUsers: viewType === VIEW_TYPES.COMPANY_ENQUIRIES ? selectedUsers : [],
    });
  };

  const handleEndDateChange = async nextEndDate => {
    setEndDate(nextEndDate);
    setSelectedOrigin("");
    setSelectedLane("");

    TrackMixPanel({
      ModuleName: "Spot",
      ScreenName: "Spot Dashboard",
      Data: ``,
      Definition: "User has changed end date filter",
    })

    if (!startDate || !nextEndDate) return;

    await fetchDashboardByDate({
      viewType,
      startDate,
      endDate: nextEndDate,
      selectedUsers:
        viewType === VIEW_TYPES.COMPANY_ENQUIRIES ? selectedUsers : [],
    });
  };

  const handleSelectedUsersChange = async (nextSelectedUsers, meta = {}) => {
    setSelectedUsers(nextSelectedUsers);
    setSelectedOrigin("");
    setSelectedLane("");
    if (!meta.isClear) {
      TrackMixPanel({
        ModuleName: "Spot",
        ScreenName: "Spot Dashboard",
        Data: ``,
        Definition: "User has changed selected users",
      })
    }

    if (viewType !== VIEW_TYPES.COMPANY_ENQUIRIES) return;
    if (!startDate || !endDate) return;

    await fetchDashboardByDate({
      viewType,
      startDate,
      endDate,
      selectedUsers: nextSelectedUsers,
    });
  };

  const handleOriginChange = async nextOrigin => {
    setSelectedOrigin(nextOrigin);
    setSelectedLane("");

    if (!startDate || !endDate) return;

    // If origin is cleared, refresh the dashboard to show all origins again
    if (!nextOrigin) {
      await fetchDashboardByDate({
        viewType,
        startDate,
        endDate,
        selectedUsers: viewType === VIEW_TYPES.COMPANY_ENQUIRIES ? selectedUsers : [],
      });
      return;
    }

    // If origin is selected, fetch location-specific data
    await fetchLocationData({
      viewType,
      startDate,
      endDate,
      selectedUsers: viewType === VIEW_TYPES.COMPANY_ENQUIRIES ? selectedUsers : [],
      filterType: LOCATION_FILTER_TYPES.ORIGIN,
      origin: nextOrigin,
    });

    TrackMixPanel({
      ModuleName: "Spot",
      ScreenName: "Spot Dashboard",
      Data: ``,
      Definition: "User has selected origin filters",
    })
  };

  const handleLaneChange = async nextLane => {
    setSelectedLane(nextLane);

    const nextLaneObject = laneOptions.find(item => item.value === nextLane);

    if (!selectedOrigin || !nextLaneObject?.destination || !startDate || !endDate) {
      return;
    }

    TrackMixPanel({
      ModuleName: "Spot",
      ScreenName: "Spot Dashboard",
      Data: ``,
      Definition: "User has selected lane filter",
    })

    await fetchLocationData({
      viewType,
      startDate,
      endDate,
      selectedUsers:
        viewType === VIEW_TYPES.COMPANY_ENQUIRIES ? selectedUsers : [],
      filterType: LOCATION_FILTER_TYPES.LANE,
      origin: selectedOrigin,
      destination: nextLaneObject.destination,
    });
  };

  const handleResetFilters = () => {
    setViewType(VIEW_TYPES.MY_ENQUIRIES);
    setSelectedUsers([]);
    setSelectedOrigin("");
    setSelectedLane("");
    setStartDate(getLastSevenDaysStartDate());
    setEndDate(getTodayDate());
    clearError();

    fetchInitialDashboard({
      viewType: VIEW_TYPES.MY_ENQUIRIES,
    });
  };

  const handleDownload = async () => {
    const element = dashRef.current;
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (clonedDoc, clonedElement) => {
          clonedElement.style.position = "static";
          clonedElement.style.overflow = "visible";

          // Disable all animations and transitions to prevent capture issues
          const style = clonedDoc.createElement("style");
          style.innerHTML = `
            * {
              animation: none !important;
              transition: none !important;
              opacity: 1 !important;
              visibility: visible !important;
            }
          `;
          clonedDoc.head.appendChild(style);

          // Hide open dropdown menus
          clonedDoc.querySelectorAll("[class*='sd-filter-menu']").forEach(el => {
            el.style.display = "none";
          });

          // Fix SVG dimensions
          clonedDoc.querySelectorAll("svg").forEach(svg => {
            const box = svg.getBoundingClientRect();
            if (box.width > 0) svg.setAttribute("width", String(box.width));
            if (box.height > 0) svg.setAttribute("height", String(box.height));
          });
        },
      });

      const A3_W_MM = 420;
      const A3_H_MM = 297;

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a3",
      });

      // Fill the entire A3 page with the image
      // pdf.addImage(imgData, "PNG", 0, 0, 420, 297);
      // pdf.save("spot-enquiry-report.pdf");
      const PX_PER_MM = 192 / 25.4;
      const imgW_mm = canvas.width / PX_PER_MM;
      const imgH_mm = canvas.height / PX_PER_MM;

      const margin = 8;
      const maxW = A3_W_MM - margin * 2;
      const maxH = A3_H_MM - margin * 2;
      const ratio = Math.min(maxW / imgW_mm, maxH / imgH_mm);
      const finalW = imgW_mm * ratio;
      const finalH = imgH_mm * ratio;

      // Centre the image on the page
      const x = (A3_W_MM - finalW) / 2;
      const y = (A3_H_MM - finalH) / 2;

      pdf.addImage(canvas.toDataURL("image/png"), "PNG", x, y, finalW, finalH);

      // Add timestamp only inside the PDF, not on screen
      const downloadedAt = new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      pdf.setFontSize(8);
      pdf.setTextColor(160, 160, 160);
      pdf.text(`Downloaded: ${downloadedAt}`, A3_W_MM - 8, A3_H_MM - 3, { align: "right" });

      pdf.save("spot-enquiry-report.pdf");

    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  const isLocationFilterApplied =
    Boolean(selectedOrigin) ||
    filters?.filter_type === LOCATION_FILTER_TYPES.ORIGIN ||
    filters?.filter_type === LOCATION_FILTER_TYPES.LANE;

  const spendByOriginData = charts.spend_by_origin || [];
  const spendByLaneOriginFilteredData = charts.spend_by_lane_origin_filtered || [];

  const spendLaneOriginChartData = isLocationFilterApplied
    ? spendByLaneOriginFilteredData
    : spendByOriginData;

  const spendLaneOriginChartTitle = isLocationFilterApplied
    ? "Spend by lane / origin"
    : "Spend by origin";

  const spendLaneOriginLabels = spendLaneOriginChartData
    .map(item => {
      if (isLocationFilterApplied) {
        return item.lane || getLaneLabel(item) || item.destination || "-";
      }

      return item.origin || "-";
    });

  const spendLaneOriginSpend = spendLaneOriginChartData
    .map(item => Number(item.spend_in_lakhs || Number(item.spend || 0) / 100000));

  const userLabels = (charts.spot_enquiries_by_user || []).map(item => shortEmail(item.email));
  const userCounts = (charts.spot_enquiries_by_user || []).map(item => Number(item.count || 0));

  const monthChartData = charts.count_of_enquiries_by_month || [];

  const monthLabels = monthChartData.map(item => item.month || "");

  const monthCounts = monthChartData.map(item =>
    Number(item.count_week_1 || 0) +
    Number(item.count_week_2 || 0) +
    Number(item.count_week_3 || 0) +
    Number(item.count_week_4 || 0) +
    Number(item.count_week_5 || 0)
  );

  const transporterRawItems = charts.spend_distribution_by_transporter || [];
  const transporterLabels = transporterRawItems.map(item => item.transporter_name || "");
  const transporterSpend = transporterRawItems.map(item =>
    Number(item.spend_in_lakhs || Number(item.spend || 0) / 100000)
  );
  const transporterTotal = transporterSpend.reduce((sum, item) => sum + item, 0);

  const savingsLabels = (charts.savings_by_transporter || []).map(item => item.transporter_name || "");
  const savingsAmounts = (charts.savings_by_transporter || []).map(item =>
    Number(item.savings_in_lakhs || Number(item.savings || 0) / 100000)
  );

  const spendDistributionSourceData = isLocationFilterApplied
    ? charts.spend_by_lane_origin_filtered || []
    : charts.spend_by_origin || [];

  const spendDistributionTitle = isLocationFilterApplied
    ? "Spend distribution by lane / origin"
    : "Spend distribution by origin";

  const TOP_N = 10;
  const CHART_SWITCH_THRESHOLD = 10;

  const buildTopNEntries = (allEntries) => {
    if (allEntries.length <= TOP_N) return allEntries;
    const top = allEntries.slice(0, TOP_N);
    const rest = allEntries.slice(TOP_N);
    const othersValue = rest.reduce((s, e) => s + e.value, 0);
    const othersSpend = rest.reduce((s, e) => s + e.spendInLakhs, 0);
    const othersPct = rest.reduce((s, e) => s + e.percentage, 0);
    return [
      ...top,
      { label: "Others", value: othersValue, spendInLakhs: othersSpend, percentage: othersPct, spend: othersSpend * 100000 },
    ];
  };

  // Full entries (for fullscreen table)
  const allSpendEntries = spendDistributionSourceData.map(item => ({
    label: isLocationFilterApplied
      ? item.lane || getLaneLabel(item) || item.destination || item.origin || "-"
      : item.origin || "-",

    // For donut chart, use percentage as value
    // Keep spend details for tooltip if needed
    value: Number(item.percentage || 0),
    spend: Number(item.spend || 0),
    spendInLakhs: Number(item.spend_in_lakhs || Number(item.spend || 0) / 100000),
    percentage: Number(item.percentage || 0),
  }));

  // Dashboard card
  const filteredSpendEntries = buildTopNEntries(allSpendEntries);

  // Transporter full entries for fullscreen table
  const allTransporterEntries = transporterRawItems.map((item, i) => ({
    label: item.transporter_name || "",
    value: transporterSpend[i],
    spendInLakhs: transporterSpend[i],
    percentage: transporterTotal > 0 ? (transporterSpend[i] / transporterTotal) * 100 : 0,
  }));

  const buildTopNTransporter = (items, spends) => {
    if (items.length <= TOP_N) return { labels: transporterLabels, spends: transporterSpend };
    const topLabels = items.slice(0, TOP_N).map(it => it.transporter_name || "");
    const topSpends = spends.slice(0, TOP_N);
    const othersSpend = spends.slice(TOP_N).reduce((s, v) => s + v, 0);
    return { labels: [...topLabels, "Others"], spends: [...topSpends, othersSpend] };
  };

  const { labels: cardTransporterLabels, spends: cardTransporterSpend } =
    buildTopNTransporter(transporterRawItems, transporterSpend);
  const cardTransporterTotal = cardTransporterSpend.reduce((s, v) => s + v, 0);

  const TOP_FS = 10;
  const buildTop5 = (allEntries) => {
    if (allEntries.length <= TOP_FS) return allEntries;
    const top = allEntries.slice(0, TOP_FS);
    const rest = allEntries.slice(TOP_FS);
    const othersValue = rest.reduce((s, e) => s + (e.value || 0), 0);
    const othersSpend = rest.reduce((s, e) => s + (e.spendInLakhs || 0), 0);
    const othersPct = rest.reduce((s, e) => s + (e.percentage || 0), 0);
    return [
      ...top,
      { label: "Others", value: othersValue, spendInLakhs: othersSpend, percentage: othersPct, spend: othersSpend * 100000 },
    ];
  };

  const fsSpendChartEntries = buildTop5(allSpendEntries);
  const fsTransporterChartEntries = buildTop5(allTransporterEntries.map(e => ({
    ...e,
    value: e.percentage,
  })));
  const fsTransporterLabels = fsTransporterChartEntries.map(e => e.label);
  const fsTransporterSpend = fsTransporterChartEntries.map(e => e.spendInLakhs);

  const anyFilter =
    viewType !== VIEW_TYPES.MY_ENQUIRIES ||
    selectedUsers.length > 0 ||
    selectedOrigin ||
    selectedLane ||
    startDate !== getLastSevenDaysStartDate() ||
    endDate !== getTodayDate();

  const currentMonthLabel = filters?.end_date
    ? new Date(filters.end_date).toLocaleString("en-US", {
      month: "short",
      year: "numeric",
    })
    : "";

  return (
    <div ref={dashRef} className={styles["sd-dashboard"]}>
      {loading && (
        <div className={styles["sd-loader-overlay"]}>
          <div className={styles["sd-spinner"]}></div>
        </div>
      )}


      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600&display=swap"
        rel="stylesheet"
      />

      {fsChart === "chart4" && (
        <FullscreenModal
          title={isLocationFilterApplied ? "Spend by lane / origin - all lanes" : "Spend by origin - top 5"}
          num={4}
          onClose={() => setFsChart(null)}
        >
          <div className={styles["sd-modal-chart-large"]}>
            <HBarChart
              labels={spendLaneOriginLabels}
              data={spendLaneOriginSpend}
              gradKey="sky"
              prefix="₹"
              suffix="L"
              visibleBars={null}
              isFullScreen={true}
            />
          </div>
        </FullscreenModal>
      )}

      {fsChart === "chart6" && (
        <FullscreenModal
          title="Spot enquiries by user - all users"
          num={6}
          onClose={() => setFsChart(null)}
        >
          <div className={styles["sd-modal-chart-large"]}>
            <VBarChart labels={userLabels} data={userCounts} gradKey="green" />
          </div>
        </FullscreenModal>
      )}

      {fsChart === "chart3" && (
        <FullscreenModal
          title="Count of enquiries by month - full timeline"
          num={3}
          onClose={() => setFsChart(null)}
        >
          <div className={styles["sd-modal-chart-fixed"]}>
            <LineChart labels={monthLabels} data={monthCounts} monthChartData={monthChartData} gradKey="sky" />
          </div>
        </FullscreenModal>
      )}

      {fsChart === "chart5" && (
        <FullscreenModal
          title={spendDistributionTitle}
          num={5}
          onClose={() => setFsChart(null)}
          tableData={allSpendEntries.map((e, i) => ({
            label: e.label,
            percentage: e.percentage,
            spendInLakhs: e.spendInLakhs,
            color: GRAD.sky[i % GRAD.sky.length],
          }))}
        >
          <DonutChart
            entries={fsSpendChartEntries}
            centerValue={formatLakhs(cards.spend_in_lakhs)}
            centerLabel="Total Spend"
          />
        </FullscreenModal>
      )}

      {fsChart === "chart1" && (
        <FullscreenModal
          title="Spend distribution by transporter"
          num={1}
          onClose={() => setFsChart(null)}
          tableData={allTransporterEntries.map((e, i) => {
            const item = (transporterRawItems || []).find(
              it => it && it.transporter_name === e.label
            );
            const hasProfile = item && item.transporter_id && e.label !== "Others";
            return {
              label: hasProfile ? (
                <ProfileModal
                  transporter_list={transporterRawItems || []}
                  transporter_id={item.transporter_id}
                  transporter_name={item.transporter_name}
                  screenName="Spot Dashboard"
                  moduleName="Spot"
                  style={{
                    fontSize: "12px",
                    color: "#2b2a29",
                    fontWeight: "400",
                    padding: "0px",
                    margin: "0px",
                    border: "none",
                    background: "transparent",
                    textAlign: "left",
                    textDecoration: "none",
                  }}
                />
              ) : e.label,
              rawLabel: e.label,
              percentage: e.percentage,
              spendInLakhs: e.spendInLakhs,
              color: GRAD.indigo[i % GRAD.indigo.length],
            };
          })}
        >
          <PieChart
            data={fsTransporterSpend}
            labels={fsTransporterLabels}
            gradKey="indigo"
            prefix="₹"
            suffix="L"
            centerValue={formatLakhs(cards.spend_in_lakhs)}
            centerLabel="Total Spend"
          />
        </FullscreenModal>
      )}

      {fsChart === "chart2" && (
        <FullscreenModal
          title="Savings by transporter"
          num={2}
          onClose={() => setFsChart(null)}
        >
          <div className={styles["sd-modal-chart-large"]}>
            <HBarChart
              labels={savingsLabels}
              data={savingsAmounts}
              profileModalItems={charts.savings_by_transporter || []}
              gradKey="teal"
              prefix="₹"
              suffix="L"
              visibleBars={null}
              isFullScreen={true}
            />
          </div>
        </FullscreenModal>
      )}

      <div className={`${styles["sd-topbar"]} ${styles["sd-row-anim"]}`}>
        <div>
          <div className={styles["sd-page-title"]}>Spot Enquiry Analytics</div>
          <div className={styles["sd-page-subtitle"]}>
            Transport operations - spend, savings &amp; carrier overview
          </div>
        </div>



        <div className={styles["sd-topbar-actions"]}>

          {error && <div className={styles["sd-error-pill"]}>{error}</div>}

          {!logoError && companySettingData && companySettingData?.logo && companySettingData?.logo !== null && companySettingData?.logo !== undefined && typeof companySettingData?.logo === "string" && companySettingData.logo.trim() !== "" && (
            <div
              className={styles["logotag"]}
              //style={{ height: "60px", width: "90px", maxWidth: "90px", borderRadius: "10px" }}
              title={companySettingData?.company_name}
            >
              <img
                src={getImageSrc(companySettingData.logo)}
                crossOrigin="anonymous"
                alt={companySettingData?.company_name || "Company logo"}
                onError={() => setLogoError(true)}
                //className="img-fluid"
                style={{ height: "60px", width: "90px", objectFit: "fit" }}
              />
            </div>
          )}

          {anyFilter && (
            <button type="button" className={styles["sd-reset-btn"]} onClick={() => {
              handleResetFilters(), TrackMixPanel({
                ModuleName: "Spot",
                ScreenName: "Spot Dashboard",
                Data: ``,
                Definition: "User has Reset filters",
              })
            }}>
              ✕ Reset filters
            </button>
          )}

          <button
            type="button"
            className={styles["sd-dl-btn"]}
            onClick={() => {
              handleDownload()
              TrackMixPanel({
                ModuleName: "Spot",
                ScreenName: "Spot Dashboard",
                Data: ``,
                Definition: "User has downloaded spot dashboard report",
              })
            }}
            title="Download"
          >
            <i className={`fa fa-long-arrow-down ${styles["sd-dl-icon"]}`} aria-hidden="true" />
          </button>

        </div>
      </div>

      <div className={`${styles["sd-filterbar"]} ${styles["sd-row-anim"]}`}>
        <span className={styles["sd-filter-label"]}>Filter by</span>

        <ViewTypeDropdown viewType={viewType} onChange={handleViewTypeChange} />

        {viewType === VIEW_TYPES.COMPANY_ENQUIRIES && (
          <FilterDropdown
            label="Select Users"
            options={userOptions}
            selected={selectedUsers}
            onChange={handleSelectedUsersChange}
            type="multi"
            clearTrackingDefinition="User has cleared select users filter"
          />
        )}

        <FilterDropdown
          label="From"
          selected={startDate}
          onChange={handleStartDateChange}
          type="date"
          max={getTodayDate()}
          min={endDate ? subtractMonthsFromDate(endDate, 3) : undefined}
        />

        <FilterDropdown
          label="To"
          selected={endDate}
          onChange={handleEndDateChange}
          type="date"
          min={startDate}
          max={getTodayDate()}
        />

        <FilterDropdown
          label="Origin / Plant"
          options={originOptions}
          selected={selectedOrigin}
          onChange={handleOriginChange}
          type="single"
          clearTrackingDefinition="User has cleared origin/plant filter"
        />

        <FilterDropdown
          label="Lane"
          options={laneOptions}
          selected={selectedLane}
          onChange={handleLaneChange}
          type="single"
          disabled={!selectedOrigin}
          clearTrackingDefinition="User has cleared lane filter"
        />
      </div>

      <div className={styles["sd-kpi-row"]}>
        <KpiCard
          label="Spot Enq. Requested"
          value={formatNumber(cards.spot_enq_requested)}
          sub={`${formatNumber(cards.spot_enq_requested)} this period`}
        />
        <KpiCard
          label="Spot Enq. Approved"
          value={formatNumber(cards.spot_enq_approved)}
          sub={`${formatPercentage(cards.spot_enq_approved_percentage)} approval rate`}
        />
        <KpiCard
          label="Truckload Requested"
          value={formatNumber(cards.truckload_requested)}
          sub="total trucks"
        />
        <KpiCard
          label="Spend in Lakhs"
          value={formatLakhs(cards.spend_in_lakhs)}
          sub={`₹${formatNumber(cards.spend)}`}
          variant="hero"
        />
        <KpiCard
          label="Accepted Volume"
          value={formatNumber(cards.accepted_volume)}
          sub="trucks accepted"
        />
        <KpiCard
          label="Accepted %"
          value={formatPercentage(cards.accepted_percentage)}
          sub="of requested"
        />
        <KpiCard
          label="Savings in Lakhs"
          value={formatLakhs(cards.saving_in_lakhs)}
          sub={`${formatPercentage(cards.saving_percentage)} saved`}
          variant="gold"
        />
        <KpiCard
          label="Transporter Win Bid Count"
          value={formatNumber(cards.transporters_won_bid_count)}
          sub="transporters won bid"
          variant="sky"
        />
      </div>

      <div className={styles["sd-charts-row1"]}>
        <ChartCard
          num={1}
          title={spendLaneOriginChartTitle}
          bodyHeight={280}
          onFullscreen={() => setFsChart("chart4")}
        >
          <HBarChart
            labels={spendLaneOriginLabels}
            data={spendLaneOriginSpend}
            gradKey="sky"
            prefix="₹"
            suffix="L"
            visibleBars={4}
          />
        </ChartCard>

        <ChartCard
          num={2}
          title="Spot enquiries by user"
          bodyHeight={280}
          onFullscreen={() => setFsChart("chart6")}
        >
          <VBarChart
            labels={userLabels}
            data={userCounts}
            gradKey="green"
            visibleBars={5}
          />
        </ChartCard>

        <ChartCard
          num={3}
          title="Count of enquiries by month"
          bodyHeight={260}
          onFullscreen={() => setFsChart("chart3")}
        >
          <LineChart labels={monthLabels} data={monthCounts} monthChartData={monthChartData} gradKey="sky" />
        </ChartCard>
      </div>

      <div className={styles["sd-charts-row2"]}>
        <ChartCard
          num={4}
          title={spendDistributionTitle}
          bodyHeight={270}
          onFullscreen={() => setFsChart("chart5")}
          legend={
            <Legend
              labels={filteredSpendEntries.map(item => item.label)}
              pcts={filteredSpendEntries.map(item => `${item.percentage.toFixed(2)}%`)}
              colors={GRAD.sky}
              maxItems={TOP_N}
            />
          }
        >
          <DonutChart
            entries={filteredSpendEntries}
            centerValue={formatLakhs(cards.spend_in_lakhs)}
            centerLabel="Total Spend"
          />
        </ChartCard>

        <ChartCard
          num={5}
          title="Spend distribution by transporter"
          bodyHeight={265}
          onFullscreen={() => setFsChart("chart1")}
          legend={
            <Legend
              labels={cardTransporterLabels.map((label, index) => {
                const item = (transporterRawItems || []).find(
                  it => it && it.transporter_name === label
                );
                if (item && item.transporter_id && label !== "Others") {
                  return (
                    <ProfileModal
                      transporter_list={transporterRawItems || []}
                      transporter_id={item.transporter_id}
                      transporter_name={item.transporter_name}
                      screenName="Spot Dashboard"
                      moduleName="Spot"
                      style={{
                        fontSize: "10px",
                        color: "#4d45a8",
                        fontWeight: "500",
                        padding: "0px",
                        margin: "0px",
                        border: "none",
                        background: "transparent",
                        textAlign: "left",
                        textDecoration: "none",
                      }}
                      key={item.transporter_id}
                    />
                  );
                }
                return label;
              })}
              pcts={cardTransporterSpend.map(value =>
                cardTransporterTotal > 0 ? `${Math.round((value / cardTransporterTotal) * 100)}%` : "0%"
              )}
              colors={GRAD.indigo}
              maxItems={TOP_N}
            />
          }
        >
          <PieChart
            data={cardTransporterSpend}
            labels={cardTransporterLabels}
            gradKey="indigo"
            prefix="₹"
            suffix="L"
            centerValue={formatLakhs(cards.spend_in_lakhs)}
            centerLabel="Total Spend"
          />
        </ChartCard>

        <ChartCard
          num={6}
          title="Savings by transporter"
          bodyHeight={280}
          onFullscreen={() => setFsChart("chart2")}
        >
          <HBarChart
            labels={savingsLabels}
            data={savingsAmounts}
            profileModalItems={charts.savings_by_transporter || []}
            gradKey="teal"
            prefix="₹"
            suffix="L"
            visibleBars={4}
          />
        </ChartCard>
      </div>
    </div>
  );
}